import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import DocumentTitle from "@/components/DocumentTitle";
import { PosterViewer, CompactCard, VerticalCard, useWidthBand } from "./feedCard";
import { IkoMix } from "@/design-system";
import { ArrowLeft, Instagram, ExternalLink } from "lucide-react";
import useSafeBack from "@/hooks/useSafeBack";
import { useAuth } from "@/components/AuthProvider";

const API = process.env.REACT_APP_BACKEND_URL;
const client = axios.create({ baseURL: `${API}/api` });

/**
 * Wave 4 #11 + #12 · one route, two rendering modes:
 *  - Free listing (kibanda_display_state ∈ {"listing", "lapsed"} or empty)
 *      · title reads "events by [name]" · upcoming + past · socials · reviews (link)
 *      · NO follow button · NO fake bio · NO "unclaimed" pill
 *  - Kibanda page  (kibanda_display_state ∈ {"trial", "active"})
 *      · title reads "[name]'s kibanda" · cover + bio + socials · follow button
 *      · tabs for posts / events / photos (only events wired in v1)
 * D30 guard · nothing on this file consults kibanda_state for ordering, scoring
 * or roses. It ONLY switches presentation.
 */
export default function OrganiserPage() {
  const { handle } = useParams();
  const navigate = useNavigate();
  const [organiser, setOrganiser] = useState(null);
  const [events, setEvents] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewer, setViewer] = useState(null);
  const [tab, setTab] = useState("events"); // kibanda only
  const [followState, setFollowState] = useState({ following: false, followers: 0 });
  const [followBusy, setFollowBusy] = useState(false);
  const { band, cols } = useWidthBand();
  const onBack = useSafeBack("/app");
  const { user } = useAuth();
  const isPaidMember = !!user && ["trialing", "founding", "paid", "comped"].includes(user.membership_state);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      client.get(`/public/organisers/${encodeURIComponent(handle)}`),
      client.get(`/public/events?time=past&past_days=180&limit=60`).catch(() => ({ data: { events: [] } })),
    ]).then(([a, pastResp]) => {
      if (!alive) return;
      const org = a.data.organiser;
      setOrganiser(org);
      setEvents(a.data.events || []);
      const canonical = (org?.handle || "").replace(/^@+/, "").toLowerCase();
      const raw = (handle || "").replace(/^@+/, "").toLowerCase();
      if (canonical && canonical !== raw) {
        navigate(`/app/organiser/${canonical}`, { replace: true });
      }
      const matchSet = new Set([canonical, ...(org?.aliases || [])]);
      setPastEvents((pastResp.data.events || []).filter((e) =>
        matchSet.has((e.organiser_handle || "").replace(/^@+/, "").toLowerCase())));
    })
      .catch(() => setOrganiser(null))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [handle, navigate]);

  const clean = (handle || "").replace(/^@+/, "").toLowerCase();
  const isKibanda = ["trial", "active"].includes(organiser?.kibanda_display_state || "");
  const isLapsed = organiser?.kibanda_display_state === "lapsed";

  useEffect(() => {
    if (!isKibanda || !clean) return;
    let alive = true;
    client.get(`/follow/counts?kind=organiser&key=${encodeURIComponent(clean)}`)
      .then((r) => alive && setFollowState((s) => ({ ...s, followers: r.data.followers || 0 }))).catch(() => {});
    const tok = localStorage.getItem("session_token");
    if (tok) {
      client.get(`/follow/status?kind=organiser&key=${encodeURIComponent(clean)}`, { headers: { Authorization: `Bearer ${tok}` } })
        .then((r) => alive && setFollowState((s) => ({ ...s, following: !!r.data.following }))).catch(() => {});
    }
    return () => { alive = false; };
  }, [isKibanda, clean]);

  const toggleFollow = async () => {
    const tok = localStorage.getItem("session_token");
    if (!tok) { window.location.assign(`/admin/login?next=/app/organiser/${clean}`); return; }
    if (!isPaidMember) { navigate("/app/membership"); return; }
    setFollowBusy(true);
    try {
      if (followState.following) {
        await client.delete(`/follow?kind=organiser&key=${encodeURIComponent(clean)}`, { headers: { Authorization: `Bearer ${tok}` } });
        setFollowState((s) => ({ following: false, followers: Math.max(0, s.followers - 1) }));
      } else {
        await client.post("/follow", { kind: "organiser", key: clean }, { headers: { Authorization: `Bearer ${tok}` } });
        setFollowState((s) => ({ following: true, followers: s.followers + 1 }));
      }
    } finally { setFollowBusy(false); }
  };

  const Card = band === "s" || band === "m" ? CompactCard : VerticalCard;
  const isDesktop = typeof window !== "undefined" && window.innerWidth >= 1024;
  const gridClassName = isDesktop ? "ww-grid-cards-3" : undefined;
  const gridStyle = isDesktop ? undefined : {
    display: "grid",
    gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
    gap: cols === 1 ? 12 : 16,
  };

  const displayName = organiser?.name ? organiser.name.toLowerCase() : `@${clean}`;
  const titleLine = isKibanda
    ? `${displayName}'s kibanda`
    : `events by ${displayName}`;

  return (
    <div className="flex h-full flex-col" data-testid="organiser-page"
         style={{ background: "var(--ww-ground)", color: "var(--ww-text)" }}>
      <DocumentTitle title={clean ? `@${clean} · watu wako` : "watu wako"} />
      <div className="flex-1 overflow-y-auto pt-4 pb-10" style={{ overscrollBehavior: "contain" }}>
        <div className="ww-container">
          <div>
            <button type="button" onClick={onBack} data-testid="organiser-back" aria-label="back"
                    style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--ww-text-muted)",
                             fontFamily: "Arial", fontSize: 13, padding: "10px 4px", minHeight: 44,
                             background: "transparent", border: 0, cursor: "pointer" }}>
              <ArrowLeft size={16} /> back
            </button>
          </div>

          {loading && <p className="py-12 text-center" style={{ fontFamily: "Arial", color: "var(--ww-text-muted)" }}>loading…</p>}

          {!loading && (
            <>
              {/* Kibanda hero · cover image (falls back to a solid tile) */}
              {isKibanda && (
                <div data-testid="kibanda-cover" style={{ height: 180, background: "var(--ww-sunken)",
                                                            position: "relative", overflow: "hidden",
                                                            borderRadius: 12, marginTop: 8 }}>
                  {organiser?.cover_image_url && (
                    <img src={organiser.cover_image_url} alt="" style={{
                      width: "100%", height: "100%", objectFit: "cover", opacity: 0.9,
                    }} />
                  )}
                  <div style={{ position: "absolute", left: 20, bottom: 12,
                                 padding: "4px 10px", borderRadius: 999,
                                 background: "rgba(26,26,48,0.72)", color: "#FFFFFF",
                                 fontFamily: "Arial", fontSize: 10, letterSpacing: "0.2em",
                                 textTransform: "uppercase", fontWeight: 700 }}>
                    kibanda
                  </div>
                </div>
              )}

              {/* Wave C.14 (Vania 7 Oct · updated by Vania's spot list) ·
                  curator header got the specific-spot spacing fix: 24px
                  under the kibanda cover (was 16) and 24px between the
                  header column and the "upcoming" h2 (was 12). */}
              <div style={isDesktop
                ? { display: "grid", gridTemplateColumns: "320px minmax(0, 1fr)", gap: 32, marginTop: 24 }
                : { marginTop: 24 }}>
                <aside style={isDesktop ? { position: "sticky", top: 24, alignSelf: "start" } : undefined}>
                  <OrganiserSidebar organiser={organiser} clean={clean} titleLine={titleLine}
                                    isKibanda={isKibanda} isLapsed={isLapsed}
                                    followState={followState} followBusy={followBusy}
                                    toggleFollow={toggleFollow} />
                </aside>
                <div style={{ minWidth: 0 }}>
              {isKibanda && (
                <nav data-testid="kibanda-tabs" className="mb-4 flex gap-2 border-b" style={{ borderColor: "var(--ww-line)" }}>
                  {["events", "posts", "photos"].map((t) => (
                    <button key={t} onClick={() => setTab(t)} data-testid={`kibanda-tab-${t}`}
                            style={{ padding: "10px 14px", border: 0, background: "transparent",
                                     fontFamily: "Poppins", fontWeight: 700, fontSize: 14,
                                     textTransform: "lowercase", cursor: "pointer",
                                     color: tab === t ? "var(--ww-text)" : "var(--ww-text-muted)",
                                     borderBottom: `2px solid ${tab === t ? "var(--ww-accent-ink)" : "transparent"}`,
                                     marginBottom: -1 }}>
                      {t}
                    </button>
                  ))}
                </nav>
              )}

              {isKibanda && tab !== "events" ? (
                <div className="rounded-2xl border p-8 text-center"
                     data-testid={`kibanda-${tab}-empty`}
                     style={{ borderColor: "var(--ww-line)", background: "var(--ww-surface)" }}>
                  <p style={{ fontFamily: "Arial", color: "var(--ww-text-muted)", fontSize: 14 }}>
                    {tab} · coming soon.
                  </p>
                </div>
              ) : (
                <>
                  <h2 className="mb-3 lowercase"
                      style={{ fontFamily: "Poppins", fontWeight: 800, letterSpacing: "-0.025em",
                               fontSize: 22, lineHeight: 1.05, color: "var(--ww-text)" }}>
                    upcoming
                  </h2>
                  {events.length === 0 ? (
                    <div className="rounded-2xl border p-8 text-center"
                         data-testid="organiser-empty"
                         style={{ borderColor: "var(--ww-line)", background: "var(--ww-surface)" }}>
                      <p style={{ fontFamily: "Arial", color: "var(--ww-text-muted)", fontSize: 14 }}>
                        no upcoming events from @{clean} yet.
                      </p>
                      <Link to="/app" data-testid="organiser-empty-cta"
                            style={{ display: "inline-block", marginTop: 12, padding: "10px 20px", borderRadius: 999,
                                     background: "#FFD60A", color: "#1A1A30",
                                     fontFamily: "Poppins", fontWeight: 600, textDecoration: "none" }}>
                        see what's on this weekend
                      </Link>
                    </div>
                  ) : (
                    <div className={gridClassName} style={gridStyle} data-testid="organiser-events-upcoming">
                      {events.map((e) => <Card key={e.id} event={e} onOpenPoster={setViewer} />)}
                    </div>
                  )}

                  {pastEvents.length > 0 && (
                    <>
                      <h2 className="mt-8 mb-3 lowercase"
                          style={{ fontFamily: "Poppins", fontWeight: 800, letterSpacing: "-0.025em",
                                   fontSize: 22, lineHeight: 1.05, color: "var(--ww-text)" }}>
                        past
                      </h2>
                      <div className={gridClassName} style={gridStyle} data-testid="organiser-events-past">
                        {pastEvents.map((e) => <Card key={e.id} event={e} onOpenPoster={setViewer} />)}
                      </div>
                    </>
                  )}
                </>
              )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      {viewer && <PosterViewer viewer={viewer} onClose={() => setViewer(null)} />}
    </div>
  );
}

function OrganiserSidebar({ organiser, clean, titleLine, isKibanda, isLapsed, followState, followBusy, toggleFollow }) {
  return (
    <header>
      <div className="flex items-center gap-3">
        {organiser?.logo_url ? (
          <img src={organiser.logo_url} alt=""
               style={{ width: 56, height: 56, borderRadius: 999, objectFit: "cover",
                        border: "2px solid var(--ww-line)" }} />
        ) : null}
        <div>
          <p style={{ fontFamily: "Arial", textTransform: "uppercase", letterSpacing: "0.22em",
                      fontSize: 11, color: "var(--ww-text-muted)" }}>
            {isKibanda ? "kibanda" : "curator"}
          </p>
          <h1 data-testid="organiser-handle" className="mt-1 lowercase"
              style={{ fontFamily: "Poppins", fontWeight: 800, letterSpacing: "-0.025em",
                       fontSize: 28, lineHeight: 1.02, color: "var(--ww-text)" }}>
            {titleLine}
          </h1>
          {organiser?.name && (
            <p style={{ fontFamily: "Arial", fontSize: 13, color: "var(--ww-text-muted)" }}>@{clean}</p>
          )}
          <p data-testid="curator-explainer" className="mt-1"
             style={{ fontFamily: "Arial", fontSize: 12, color: "var(--ww-text-muted)",
                      maxWidth: 420, lineHeight: 1.4 }}>
            curators are the people behind the events.
          </p>
        </div>
      </div>

      {isKibanda && organiser?.bio && (
        <p data-testid="kibanda-bio" className="mt-3"
           style={{ fontFamily: "Arial", fontSize: 14, color: "var(--ww-text-muted)" }}>
          {organiser.bio}
        </p>
      )}

      <div className="mt-3">
        <IkoMix counts={organiser?.iko_mix?.counts || {}} total={organiser?.iko_mix?.total || 0}
                overrideChips={organiser?.iko_chip_override} label="usually runs"
                testId="organiser-iko-mix" />
      </div>

      <div className="mt-3 flex flex-wrap gap-2 items-center">
        {organiser?.instagram_url && (
          <a href={organiser.instagram_url} target="_blank" rel="noreferrer"
             data-testid="organiser-ig"
             style={socialPill}>
            <Instagram size={14} /> instagram
          </a>
        )}
        {organiser?.website && (
          <a href={organiser.website} target="_blank" rel="noreferrer"
             data-testid="organiser-website"
             style={socialPill}>
            <ExternalLink size={14} /> website
          </a>
        )}

        {isKibanda && (
          <button data-testid="kibanda-follow" onClick={toggleFollow} disabled={followBusy}
                  style={{ padding: "10px 16px", borderRadius: 999,
                           background: followState.following ? "transparent" : "var(--ww-text)",
                           color: followState.following ? "var(--ww-text)" : "var(--ww-ground)",
                           border: `1px solid ${followState.following ? "var(--ww-line-strong)" : "var(--ww-text)"}`,
                           fontFamily: "Poppins", fontWeight: 600, fontSize: 13, textTransform: "lowercase",
                           cursor: followBusy ? "wait" : "pointer", minHeight: 44 }}>
            {followState.following ? `following · ${followState.followers}` : `follow · ${followState.followers}`}
          </button>
        )}
      </div>

      {isLapsed && (
        <p data-testid="organiser-lapsed-note" className="mt-3 rounded-md border p-2"
           style={{ borderColor: "var(--ww-line)", background: "var(--ww-surface)",
                    fontFamily: "Arial", fontSize: 12, color: "var(--ww-text-muted)" }}>
          this curator is on the free listing tier.
        </p>
      )}
    </header>
  );
}

const socialPill = {
  display: "inline-flex", alignItems: "center", gap: 6,
  padding: "10px 14px", borderRadius: 999,
  border: "1px solid var(--ww-line-strong)", color: "var(--ww-text)",
  fontFamily: "Poppins", fontWeight: 600, textDecoration: "none",
  fontSize: 13, minHeight: 44,
};
